import { Injectable } from '@angular/core';
import {
  ConfigBaseFilter,
  ListLayout,
  ListLayoutMultipleComponent,
  ListSortOption,
  SimpleConfigBaseFilter
} from 'gung-list';
import {
  AssortmentService,
  AuthService,
  CloudPimFiltersService,
  CurrentAssortmentService,
  GungFlowService,
  LocationConfigService,
  MetadataService,
  Product,
  ProductArticleClassFilterService,
  ProductService,
  ProductSortService
} from 'gung-standard';
import { JeevesProductListConfigService, ProductItemGroupFilterService } from 'gung-standard-jeeves';
import { filter, first, map, Observable, switchMap, tap } from 'rxjs';
import { CultJeevesProductTableComponent } from '../components/cult-jeeves-product-table/cult-jeeves-product-table.component';
import { CultProductCardComponent } from '../components/cult-product-card/cult-product-card.component';
import { CultArQEliassonArtnrFilter } from './cult-product-list-v2-config.service';
import { Router } from '@angular/router';

class ItemTypeCd2Filter extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
    this.isPostFilter = () => false;
  }
  getName(): string {
    return 'MAIN_CATEGORY';
  }

  getOptionIds(item: Product): string[] {
    return [item.extra.ar.itemtypecd2];
  }
  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('atp2', 'itemtypedesc', key);
  }
}
class ItemTypeCd3Filter extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
  }
  getName(): string {
    return 'SUB_CATEGORY_2';
  }

  getOptionIds(item: Product): string[] {
    return [item.extra.ar.itemtypecd3];
  }
  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('atp3', 'itemtypedesc', key);
  }
}
class ItemTypeCd4Filter extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
  }
  getName(): string {
    return 'CAMPAIGN';
  }

  getOptionIds(item: Product): string[] {
    if (!item.extra.ar.itemtypecd4) {
      return ['PRODUCTS_NOT_IN_CAMPAIGN'];
    }
    return ['' + item.extra.ar.itemtypecd4];
  }
  getOptionName(key: string): string {
    return this.metadataService.getMetadataValue('atp4', 'itemtypedesc', key);
  }
}

export class CultProductArticleCategoryFilterService extends SimpleConfigBaseFilter<Product> {
  constructor(protected metadataService: MetadataService) {
    super();
  }

  getOptionIds(item: Product): string[] {
    const id = item.extra?.ar?.artkod;
    return [id];
  }
  getOptionName(key: string): string {
    const arttypbeskr = this.metadataService.getMetadataValue('xm', 'arttypbeskr', key);
    const webbesk = this.metadataService.getMetadataValue('xm', 'webbeskr', key);
    return webbesk ? webbesk : arttypbeskr;
  }
  getName(): string {
    return 'ARTICLE_CATEGORY';
  }
}

@Injectable({
  providedIn: 'root'
})
export class CultProductListConfigService extends JeevesProductListConfigService {
  filters;
  topFilter = true;
  isReturnFlow = false;
  currentAssortment;

  constructor(
    protected authService: AuthService,
    protected productService: ProductService,
    protected productItemGroupFilterService: ProductItemGroupFilterService,
    protected productArticleClassFilterService: ProductArticleClassFilterService,
    protected metadataService: MetadataService,
    protected productSortService: ProductSortService,
    protected gungFlowService: GungFlowService,
    protected locationConfigService: LocationConfigService,
    protected cloudpimFilters: CloudPimFiltersService,
    protected currentAssortmentService: CurrentAssortmentService,
    protected assortmentService: AssortmentService,
    protected router: Router
  ) {
    super(
      authService,
      productService,
      productItemGroupFilterService,
      productArticleClassFilterService,
      productSortService,
      gungFlowService,
      locationConfigService,
      cloudpimFilters,
      currentAssortmentService
    );

    this.gungFlowService.getSelectedFlow().subscribe(flow => {
      if (flow.isReturnOrder) {
        this.isReturnFlow = true;
      } else {
        this.isReturnFlow = false;
      }
    });
  }

  getItems(): Observable<Product[]> {
    return this.assortmentSubject.pipe(
      switchMap((assortment: string) => {
        if (assortment == null) {
          return this.authService.getCurrentUser().pipe(
            filter(user => !!user),
            tap(user => {
              this.assortmentService
                .getAssortmentRecursive(user && user.assortment)
                .pipe(first())
                .subscribe(assort => {
                  this.filters = this.cloudpimFilters.getFilters(assort);
                  this.currentAssortment = assort;
                });
            }),
            switchMap(user => this.fetchProducts(user && user.assortment))
          );
        }

        // If user in product export page get assortment from user
        if (this.router.url.includes('/products-export')) {
          return this.authService.getCurrentUser().pipe(
            filter(user => !!user),
            tap(user => {
              this.assortmentService
                .getAssortmentRecursive(user && user.assortment)
                .pipe(first())
                .subscribe(assort => {
                  this.filters = this.cloudpimFilters.getFilters(assort);
                  this.currentAssortment = assort;
                });
            }),
            switchMap(user => this.fetchProducts(user && user.assortment))
          );
        }

        this.assortmentService
          .getAssortmentRecursive(assortment)
          .pipe(first())
          .subscribe(assort => {
            this.filters = this.cloudpimFilters.getFilters(assort);
            this.currentAssortment = assort;
          });
        return this.fetchProducts(assortment);
      }),
      map(m =>
        m.sort((a, b) => {
          // SORT by pim sorting Priority
          const aSort = a.extra.pim && a.extra.pim.sequence;
          const bSort = b.extra.pim && b.extra.pim.sequence;
          if ((!bSort && aSort) || (aSort && bSort && Number(aSort) > Number(bSort))) {
            return -1;
          } else if ((!aSort && bSort) || (aSort && bSort && Number(aSort) < Number(bSort))) {
            return 1;
          } else {
            return 0;
          }
        })
      )
    );
  }

  getFilters(): ConfigBaseFilter<Product>[] {
    /* return [
      new ItemTypeCd2Filter(this.metadataService),
      new ItemTypeCd3Filter(this.metadataService),
      new ItemTypeCd4Filter(this.metadataService)
    ]; */
    if (this.isReturnFlow) {
      return [];
    }
    return [...this.cloudpimFilters.getFilters(this.currentAssortment, undefined, undefined, true), new CultArQEliassonArtnrFilter()];
  }

  getBatchSizes() {
    return [48];
  }

  getLayouts(): ListLayout<Product>[] {
    return [
      {
        getIconClass: () => 'fas fa-th',
        getListItemComponent: () => CultProductCardComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductGrid'
      },
      {
        getIconClass: () => 'fa fa-list',
        getListItemComponent: () => CultJeevesProductTableComponent,
        getListLayoutComponent: () => ListLayoutMultipleComponent,
        getName: () => 'ProductList'
      }
    ];
  }

  hideZeroOptions() {
    return true;
  }

  getSearchTerms(item: Product) {
    return [item.id, item.name, item.extra.pim?.ar_q_wa_eankod_konsument + ''];
  }

  getSortOptions(): ListSortOption<Product>[] {
    return [
      {
        getLabel: () => 'PIM_SEQUENCE',
        getComparator: () => (item1: Product, item2: Product) =>
          this.sequenceSort(item1?.extra?.pim?.sequence, item2?.extra?.pim?.sequence, -1)
      },
      {
        getLabel: () => 'NAME_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.name, item2?.name, 1)
      },
      {
        getLabel: () => 'NAME_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.name, item2?.name, -1)
      },
      {
        getLabel: () => 'ITEM_NO_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.id, item2?.id, 1)
      },
      {
        getLabel: () => 'ITEM_NO_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.comparatorHelper(item1?.id, item2?.id, -1)
      },
      {
        getLabel: () => 'ITEM_GROUP_ASC',
        getComparator: () => (item1: Product, item2: Product) => this.itemGroupSort(item1, item2, 1)
      },
      {
        getLabel: () => 'ITEM_GROUP_DESC',
        getComparator: () => (item1: Product, item2: Product) => this.itemGroupSort(item1, item2, -1)
      }
    ];
  }

  comparatorHelper(aValue, bValue, ascSort) {
    if (!aValue && !bValue) {
      return 0;
    }
    if (!aValue) {
      return 1 * ascSort;
    }
    if (!bValue) {
      return -1 * ascSort;
    }
    let aNumber;
    let bNumber;
    if (typeof aValue === 'number') {
      aNumber = aValue;
    }
    if (typeof bValue === 'number') {
      bNumber = bValue;
    }
    if (!aNumber && !isNaN(Number(aValue.replace(',', '.')))) {
      aNumber = Number(aValue.replace(',', '.'));
    }
    if (!bNumber && !isNaN(Number(bValue.replace(',', '.')))) {
      bNumber = Number(bValue.replace(',', '.'));
    }
    // Number
    if (aNumber && bNumber) {
      if (Number(aNumber) < Number(bNumber)) {
        return -1 * ascSort;
      }
      if (Number(bNumber) < Number(aNumber)) {
        return 1 * ascSort;
      }
      return 0;
    }
    // String
    return aValue.localeCompare(bValue) * ascSort;
  }

  sequenceSort(aValue, bValue, ascSort) {
    if (!aValue && !bValue) {
      return 0;
    }
    if (!aValue) {
      return 1;
    }
    if (!bValue) {
      return -1;
    }
    let aNumber;
    let bNumber;
    if (typeof aValue === 'number') {
      aNumber = aValue;
    }
    if (typeof bValue === 'number') {
      bNumber = bValue;
    }
    if (!aNumber && !isNaN(Number(aValue.replace(',', '.')))) {
      aNumber = Number(aValue.replace(',', '.'));
    }
    if (!bNumber && !isNaN(Number(bValue.replace(',', '.')))) {
      bNumber = Number(bValue.replace(',', '.'));
    }
    // Number
    if (aNumber && bNumber) {
      return ascSort * (aNumber < bNumber ? -1 : 1);
    }
    // String
    return aValue.localeCompare(bValue) * ascSort;
  }

  itemGroupSort(a, b, ascSort) {
    if (a.extra?.pim?.ar_varugruppkod?.description && b.extra?.pim?.ar_varugruppkod?.description) {
      return (
        a.extra.pim?.ar_varugruppkod?.description.localeCompare(b.extra.pim?.ar_varugruppkod?.description) * ascSort
      );
    }
    if (a.extra?.pim?.ar_varugruppkod?.description) {
      return -1 * ascSort;
    }
    if (b.extra?.pim?.ar_varugruppkod?.description) {
      return 1 * ascSort;
    }
    return 0;
  }
}
